/**
 * CR-5965: single source of truth for the Supplier Onboarding stage counts.
 *
 * The four statistic tiles at the top of the page used to be served by
 * `GET /onboarding/dashboard/`, which counts `OnboardingWorkflow` /
 * `SupplierInvitation` rows. The tabs underneath are built from a different
 * set of records entirely — the Approved tab lists `SupplierProfile`
 * (`is_approved=true`), Pending Due Diligence unions `FilledDataset` across
 * four statuses (CR-4799) plus `SupplierQualification` rows (CR-5984) plus
 * orphan workflows, and Invited applies the CR-5953 mutual-exclusivity rules.
 *
 * Every rework of the tabs (CR-4799, CR-5953, CR-5984, CR-6507) moved the tabs
 * without moving the tile queries, so the two drifted apart: QA saw "0 Approved"
 * against a tab listing 13 suppliers. Reproduced locally as a group user with
 * an Approved tile of 1 against a tab of 15.
 *
 * Rather than keep two definitions of each stage in sync — which has now failed
 * twice — the tiles are derived from the very arrays the tabs render, so
 * "tile count == records visible in the tab" holds by construction.
 *
 * Counts are taken BEFORE the search filter: the search box narrows the tab
 * listing, but the tiles describe the pipeline as a whole and must not move
 * while the user is typing.
 */
import type {
  SupplierInvitation,
  OnboardingWorkflow,
  SupplierProfile,
  SupplierQualification,
  PendingInitialSupplier,
} from '../../lib/api';
import { groupPendingDueDiligenceSuppliers, deriveOrphanWorkflows } from './onboardingSelectors';
import type { FilledDatasetWithDetails, InvitedStageInvitation } from './onboardingSelectors';

/** Workflow statuses that mean "invited but not yet through due diligence". */
export const WIP_WORKFLOW_STATUSES = ['invited', 'registered', 'documents_pending', 'questionnaires_pending'];

export interface InvitedStageContext {
  /** Suppliers that have submitted questionnaires — they belong to PDD, not Invited. */
  suppliersInDueDiligence: Set<number>;
  /** Approved suppliers — approval ends the process wherever it came from. */
  approvedSupplierIds: Set<number>;
  /** Suppliers whose workflow is still pre-submission (drives the "WIP" badge). */
  wipSupplierIds: Set<number>;
}

/**
 * The Invited tab's membership rule, extracted so the tab listing and the
 * Pending Invitations tile apply exactly the same predicate.
 *
 * CR-5953 / CR-5953 rework: Invited and Pending Due Diligence are mutually
 * exclusive. A supplier who has submitted questionnaires, or who has been
 * approved, drops out of Invited. Accepted invitations stay in Invited — shown
 * as "wip" — until their questionnaires are submitted.
 *
 * @param invitations - invitations to select from (unfiltered for the tile,
 *   search-filtered for the tab listing)
 * @param ctx - the cross-referenced supplier id sets
 * @returns invitations in the Invited stage, with accepted rows relabelled 'wip'
 */
export function selectInvitedStageInvitations(
  invitations: SupplierInvitation[],
  ctx: InvitedStageContext,
): InvitedStageInvitation[] {
  return invitations
    .filter(i =>
      (i.supplier == null
        || (!ctx.suppliersInDueDiligence.has(i.supplier) && !ctx.approvedSupplierIds.has(i.supplier))) &&
      (
        ['sent', 'opened', 'pending'].includes(i.status) ||
        // invitation.supplier is optional on the BE — only widen if we have a
        // supplier id to cross-reference against the workflow set.
        (i.status === 'accepted' && i.supplier != null && ctx.wipSupplierIds.has(i.supplier))
      )
    )
    .map((i): InvitedStageInvitation => (
      i.status === 'accepted' ? { ...i, status: 'wip' } : i
    ));
}

export interface StageCountInputs {
  invitations: SupplierInvitation[];
  workflows: OnboardingWorkflow[];
  pendingReviews: FilledDatasetWithDetails[];
  submittedQualifications: SupplierQualification[];
  approvedSuppliers: SupplierProfile[];
  pendingInitialSuppliers: PendingInitialSupplier[];
  pendingApprovals: SupplierInvitation[];
  invitedStage: InvitedStageContext;
}

export interface OnboardingStageCounts {
  /** Invited tab: invitations in flight + accepted-but-not-yet-submitted (WIP). */
  pendingInvitations: number;
  /** Total invitations raised, for the tile's sub-caption. */
  totalInvitations: number;
  /**
   * Distinct suppliers mid-pipeline = Invited stage + Pending Due Diligence
   * stage. Deduplicated by supplier, since one supplier can hold several
   * invitation rows (re-invited, or invited by more than one of the viewer's
   * companies) and the tile is captioned as a supplier count.
   */
  activeOnboardings: number;
  /** Pending Due Diligence tab: distinct suppliers awaiting review. */
  pendingDueDiligence: number;
  /** Approved tab: fully onboarded supplier profiles. */
  approved: number;
}

/**
 * Compute the four tile counts from the same records the tabs render.
 *
 * Active Onboardings has no tab of its own, so it is defined as the two
 * in-flight stages combined, counted per supplier. CR-5953 makes Invited and
 * Pending Due Diligence mutually exclusive, so adding the two stages cannot
 * double-count a supplier across them.
 *
 * @param input - the unfiltered arrays backing each tab
 * @returns the counts to render in the statistic tiles
 * @link groupPendingDueDiligenceSuppliers - the PDD tab's own grouping
 */
export function computeOnboardingStageCounts(input: StageCountInputs): OnboardingStageCounts {
  const invitedStageInvitations = selectInvitedStageInvitations(input.invitations, input.invitedStage);

  const invitedStageSupplierIds = new Set<number>(
    invitedStageInvitations.map(i => i.supplier).filter((s): s is number => s != null)
  );
  const pendingApprovalSupplierIds = new Set<number>(
    [
      ...input.pendingInitialSuppliers.map(p => p.company_id),
      ...input.pendingApprovals.map(i => i.supplier),
    ].filter((s): s is number => s != null)
  );
  const suppliersWithQualifications = new Set<number>(
    input.submittedQualifications.map(q => q.supplier)
  );

  // Same orphan derivation the tab uses, with the search predicate opened up
  // so the tile counts the whole pipeline rather than the current search hit.
  const orphanWorkflows = deriveOrphanWorkflows(input.workflows, {
    suppliersWithQualifications,
    invitedStageSupplierIds,
    pendingApprovalSupplierIds,
    matchesSearch: () => true,
  });

  const pendingDueDiligence = groupPendingDueDiligenceSuppliers(
    input.pendingReviews,
    input.submittedQualifications,
    orphanWorkflows,
  ).length;

  // Invited-stage suppliers, deduplicated: a supplier can hold more than one
  // invitation row, and the Invited tab lists each row. That is right for the
  // Pending Invitations tile (it mirrors the tab) but would inflate Active
  // Onboardings, which is captioned "Suppliers completing onboarding steps".
  // Rows with no supplier id are distinct pipeline entries in their own right.
  const invitedStageSupplierCount =
    invitedStageSupplierIds.size
    + invitedStageInvitations.filter(i => i.supplier == null).length;

  return {
    pendingInvitations: invitedStageInvitations.length,
    totalInvitations: input.invitations.length,
    activeOnboardings: invitedStageSupplierCount + pendingDueDiligence,
    pendingDueDiligence,
    approved: input.approvedSuppliers.length,
  };
}
